import { useEffect, useRef } from "react";
import { useImmer } from "use-immer";
import { useDebouncedCallback } from "use-debounce";
import { Button, Stack } from "@mui/material";
import qs from "qs";
import {
  addRACTMember,
  deleteRACTMembers,
  downloadRACTMembers,
  downloadRACTSample,
  getRACTMembers,
  importRACTMembers,
} from "../apis";
import { errorMessage, successMessage } from "../../../utils";
import { useAlertContext } from "../../../shared";
import { initialAlertState } from "../../../store";
var fileDownload = require("js-file-download");

const toMember = (member) => ({
  id: member.member_id ?? member.id,
  member_number: String(member.member_id ?? member.member_number ?? ""),
});

export const useRACTActiveMembers = () => {
  const { setAlert } = useAlertContext();
  const importFileRef = useRef(null);

  const [state, setState] = useImmer({
    isLoading: true,
    members: [],
    searchText: "",
    pagination: {
      pageIndex: 1,
      pageSize: 50,
      pageCount: 0,
      total: 0,
      hasMorePages: true,
      lastPage: 1,
    },
    addModal: {
      isOpen: false,
      memberNumber: "",
      isSaving: false,
    },
    isImporting: false,
  });

  useEffect(() => {
    getMembers({ search: "", pagination: state.pagination });
  }, []);

  const getMembers = async ({ search, pagination }) => {
    const query = {
      params: search ? { search } : {},
      paramsSerializer: (params) => qs.stringify(params),
    };

    setState((draft) => {
      draft.isLoading = true;
    });
    try {
      const res = await getRACTMembers(query, pagination);
      if (res.success) {
        setState((draft) => {
          draft.members = (res.data.members ?? []).map(toMember);
          draft.pagination = res.data.pagination ?? draft.pagination;
        });
      }
    } catch (err) {
      errorMessage(err.response?.data?.message || "Failed to fetch members");
    } finally {
      setState((draft) => {
        draft.isLoading = false;
      });
    }
  };

  const refreshMembers = (pageIndex = state.pagination.pageIndex) => {
    getMembers({
      search: state.searchText,
      pagination: { pageIndex, pageSize: state.pagination.pageSize },
    });
  };

  const gotoPage = (pageIndex) => refreshMembers(pageIndex);

  const setPageSize = (e) => {
    getMembers({
      search: state.searchText,
      pagination: { pageIndex: 1, pageSize: e.target.value },
    });
  };

  const debouncedSearch = useDebouncedCallback((value) => {
    getMembers({
      search: value,
      pagination: { pageIndex: 1, pageSize: state.pagination.pageSize },
    });
  }, 1000);

  const onSearchChange = (value) => {
    setState((draft) => {
      draft.searchText = value;
    });
    debouncedSearch(value);
  };

  const handleAddModal = (isOpen) => {
    setState((draft) => {
      draft.addModal.isOpen = isOpen;
      draft.addModal.memberNumber = "";
    });
  };

  const onChangeMemberNumber = (value) => {
    setState((draft) => {
      draft.addModal.memberNumber = value;
    });
  };

  const handleAddMember = async () => {
    setState((draft) => {
      draft.addModal.isSaving = true;
    });
    try {
      const res = await addRACTMember({
        member_id: state.addModal.memberNumber.trim(),
      });
      if (res.success) {
        successMessage(res.message || "Member added successfully");
        handleAddModal(false);
        refreshMembers(1);
      } else {
        throw new Error(res.message);
      }
    } catch (err) {
      errorMessage(err.response?.data?.message || err.message);
    } finally {
      setState((draft) => {
        draft.addModal.isSaving = false;
      });
    }
  };

  const deleteMembers = async (memberIds) => {
    try {
      const res = await deleteRACTMembers({ member_ids: memberIds });
      if (res.success) {
        successMessage(res.message || "Deleted Successfully");
        // Step back a page when the last members of the current page are removed.
        const isPageEmptied =
          memberIds.length >= state.members.length &&
          state.pagination.pageIndex > 1;
        refreshMembers(
          isPageEmptied
            ? state.pagination.pageIndex - 1
            : state.pagination.pageIndex,
        );
      } else {
        throw new Error(res.message);
      }
    } catch (err) {
      errorMessage(err.response?.data?.message || err.message);
    }
  };

  const handleCloseAlert = () => setAlert(initialAlertState);

  const showDeleteAlert = (memberIds) => {
    const isMultiple = memberIds.length > 1;
    setAlert((draft) => {
      draft.open = true;
      draft.title = isMultiple ? "Delete Member Numbers" : "Delete Member Number";
      draft.description = isMultiple
        ? `Are you sure you want to Delete the ${memberIds.length} selected Member Numbers?`
        : "Are you sure you want to Delete the Member Number?";
      draft.rowAction = (
        <Stack flexDirection="row" gap="12px">
          <Button onClick={handleCloseAlert}>No</Button>
          <Button
            variant="contained"
            onClick={async () => {
              handleCloseAlert();
              await deleteMembers(memberIds);
            }}
          >
            Yes
          </Button>
        </Stack>
      );
      draft.sx = { width: 425 };
    });
  };

  const handleBulkDelete = (selectedRows) => {
    showDeleteAlert(selectedRows.map((row) => row.original.id));
  };

  const openImportPicker = () => importFileRef.current?.click();

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setState((draft) => {
      draft.isImporting = true;
    });
    try {
      const res = await importRACTMembers(file);
      if (res.success) {
        successMessage(res.message || "Members imported successfully");
        refreshMembers(1);
      } else {
        throw new Error(res.message);
      }
    } catch (err) {
      errorMessage(err.response?.data?.message || err.message);
    } finally {
      setState((draft) => {
        draft.isImporting = false;
      });
      // Allow picking the same file again.
      e.target.value = "";
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await downloadRACTSample();
      fileDownload(res.data, "ract_template.csv", "text/csv");
    } catch (err) {
      errorMessage(err.response?.data?.message || "Failed to download template");
    }
  };

  const handleExport = async () => {
    try {
      const res = await downloadRACTMembers();
      fileDownload(res.data, "ract_members.csv", "text/csv");
    } catch (err) {
      errorMessage(err.response?.data?.message || "Failed to export members");
    }
  };

  return {
    state,
    importFileRef,
    gotoPage,
    setPageSize,
    onSearchChange,
    handleAddModal,
    onChangeMemberNumber,
    handleAddMember,
    showDeleteAlert,
    handleBulkDelete,
    openImportPicker,
    handleImport,
    handleDownloadTemplate,
    handleExport,
  };
};
